/**
 * CombatLogEvent — discriminated union for client-side normalized combat
 * events. Single source-of-truth shape consumed by the chat-bridge today
 * and by future killfeed / damage-meter / replay-tooling consumers.
 *
 * The wire-side payloads (server `playerDamaged`, `npcDamaged`, etc.)
 * are heterogeneous and noun-typed by convention. The CombatLogService
 * normalizes them into this union so consumers don't reach into the
 * wire shapes themselves — name resolution, kind classification, and
 * coalescing happen ONCE in the service, not per-consumer.
 *
 * Adding a new event kind:
 *   1. Extend the union below with a new `{ kind: ... }` branch
 *   2. Add the matching wire→event normalizer in CombatLogService
 *   3. Add a formatter case in CombatLogFormatter (plus a test row)
 *   4. Subscribers receive it via the existing pub/sub — no API change
 *
 * No runtime side-effects in this file — pure types + tiny helpers
 * (sourceKindOf, isHostileTo, etc.) so it stays consumable from server
 * code if/when a logging mirror lands. Uses the same `AbilitySchool`
 * union as `CombatFeedbackConfig.ts` so school strings are consistent
 * across the combat-log + floating-text + screen-effect pipelines.
 */

import type { AbilitySchool } from '../constants/CombatFeedbackConfig';

/**
 * What kind of actor produced or received an event. Bots are players
 * on the wire (entries in `state.players` keyed by `bot_*` sids) but
 * formatters often want to distinguish "Bot Brick" from "DJ" — keep
 * the distinction at the type level.
 */
export type ActorKind = 'player' | 'bot' | 'npc' | 'unknown';

/** Common header on every combat-log event. */
interface CombatLogEventBase {
  /** Wall-clock millis when the service normalized the wire event. */
  ts: number;
  /**
   * Coalescing multiplier. 1 = single event. >1 = N identical events
   * (same kind/source/target/ability) collapsed within the throttle
   * window. The formatter renders `(×N)` when > 1.
   */
  multiplier: number;
}

/** Damage event — the workhorse. Mapped from `playerDamaged` + `npcDamaged`. */
export interface CombatLogDamageEvent extends CombatLogEventBase {
  kind: 'damage';
  sourceId: string;
  sourceName: string;
  sourceKind: ActorKind;
  targetId: string;
  targetName: string;
  targetKind: ActorKind;
  /** Ability ID from wire (or `null` for raw auto-attacks). */
  ability: string | null;
  /** Resolved via `getAbilityData()` at normalize time. `null` if unknown. */
  abilityName: string | null;
  school: AbilitySchool;
  amount: number;
  critical: boolean;
  /** Target HP after the hit landed (mirrors wire). */
  remainingHp: number;
  maxHp: number;
}

/**
 * Heal event — shape only, no producer yet. Server's `applyHealing()`
 * (CombatSystem.ts:169) doesn't broadcast today; L7 friendly-heals
 * phase will. Defining the shape now means L7 only emits to the
 * existing service — no rework on consumers.
 */
export interface CombatLogHealEvent extends CombatLogEventBase {
  kind: 'heal';
  sourceId: string;
  sourceName: string;
  sourceKind: ActorKind;
  targetId: string;
  targetName: string;
  targetKind: ActorKind;
  ability: string | null;
  abilityName: string | null;
  amount: number;
  /** Effective heal beyond the target's current HP (cosmetic; if known). */
  overheal: number;
  critical: boolean;
}

/** PvP kill — derived from `playerDied` broadcast. */
export interface CombatLogKillEvent extends CombatLogEventBase {
  kind: 'kill';
  sourceId: string | null;       // null = environmental / suicide
  sourceName: string;
  sourceKind: ActorKind;
  targetId: string;
  targetName: string;
  targetKind: ActorKind;          // 'player' | 'bot' for PvP kills
  isPvP: true;
}

/** NPC death — derived from `npcDefeated` broadcast. No respawn signal. */
export interface CombatLogNpcDeathEvent extends CombatLogEventBase {
  kind: 'npc_death';
  npcId: string;
  npcName: string;
  /** If the broadcast carries a credited attacker, surface them. */
  defeatedById: string | null;
  defeatedByName: string | null;
  defeatedByKind: ActorKind;
  xpGained: number;
}

/** Ability cast — derived from `abilityCasted` broadcast. */
export interface CombatLogCastEvent extends CombatLogEventBase {
  kind: 'cast';
  sourceId: string;
  sourceName: string;
  sourceKind: ActorKind;
  ability: string;
  abilityName: string;
  targetId: string | null;
  targetName: string | null;
}

/** Boss-phase transition — derived from `bossPhaseChange` broadcast. */
export interface CombatLogPhaseEvent extends CombatLogEventBase {
  kind: 'phase';
  npcId: string;
  npcName: string;
  phaseName: string;
  hpThreshold: number;
}

/** Player respawn — derived from `playerRespawned` broadcast. */
export interface CombatLogRespawnEvent extends CombatLogEventBase {
  kind: 'respawn';
  targetId: string;
  targetName: string;
  targetKind: ActorKind;
}

export type CombatLogEvent =
  | CombatLogDamageEvent
  | CombatLogHealEvent
  | CombatLogKillEvent
  | CombatLogNpcDeathEvent
  | CombatLogCastEvent
  | CombatLogPhaseEvent
  | CombatLogRespawnEvent;

/**
 * Classify a sid into an `ActorKind`. Bots are auto-spawned with
 * `bot_<class>_<n>` ids (see `BotManager.spawnBot:83`); other player
 * sids are Colyseus session ids. NPC ids come from the NPC schema map.
 *
 * Caller passes the id space hint when available (e.g. CombatLogService
 * already knows whether it pulled the id from `state.players` or
 * `state.npcs`); the heuristic below is the fallback.
 */
export function classifyActor(
  id: string | null | undefined,
  source: 'players' | 'npcs' | 'unknown' = 'unknown',
): ActorKind {
  if (!id) return 'unknown';
  if (source === 'npcs') return 'npc';
  if (id.startsWith('bot_')) return 'bot';
  if (source === 'players') return 'player';
  return 'unknown';
}

/**
 * The "viewer" of a combat-log line — used by formatters to pivot
 * "Brick hit Lysa" vs "Brick hit you". `null` means third-party view
 * (no first-person substitution).
 */
export type ViewerSid = string | null;
